import React, { useEffect, useState } from "react";
import { Alert, Platform, StyleSheet, TouchableOpacity } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { gql, useMutation } from "@apollo/client";
import { MaterialIcons } from "@expo/vector-icons";
import { Text, View } from "./Themed";
import Config from "../constants/Config";
import useColorScheme from "../hooks/useColorScheme";

const ONLINE_DELIVERY_FIELDS = gql`
  fragment OnlineDeliveryQueueFields on Delivery {
    id
    publicToken
    orderNumber
    location
    source
    channel
    status
    paymentMethod
    paymentStatus
    createdAt
    updatedAt
    acceptedAt
    completedAt
    customer { name phone email }
    fulfillment {
      pickupTime
      city
      street
      comment
      deliveryInstructions
      deliveryFee
    }
    items {
      productId
      title
      variant
      quantity
      unitGrossPrice
      grossTotal
      optionTitles
      comment
    }
    totals { itemsGross deliveryFee orderGross }
  }
`;

const CHANGE_ONLINE_ORDER_STATUS = gql`
  mutation ChangeOnlineOrderStatus($token: String!, $operator: ID!, $orderId: ID!, $status: String!) {
    changeOnlineOrderStatus(token: $token, operator: $operator, orderId: $orderId, status: $status) {
      id
      status
      acceptedAt
      cancelledAt
      updatedAt
    }
  }
`;

const COMPLETE_ONLINE_ORDER = gql`
  mutation CompleteOnlineOrder($token: String!, $operator: ID!, $orderId: ID!, $paymentType: String!) {
    completeOnlineOrder(token: $token, operator: $operator, orderId: $orderId, paymentType: $paymentType) {
      id
      status
      paymentStatus
      paymentType
      completedAt
      paidAt
    }
  }
`;

const STATUS_LABELS = {
  awaiting_contact_confirmation: "Awaiting confirmation",
  submitted_unpaid: "Submitted",
  awaiting_payment: "Awaiting payment",
  accepted: "Accepted",
  preparing: "Preparing",
  ready: "Ready",
  out_for_delivery: "Out for delivery",
  completed: "Completed",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

const ACTIVE_STATUSES = new Set([
  "submitted_unpaid",
  "awaiting_payment",
  "accepted",
  "preparing",
  "ready",
  "out_for_delivery",
]);

function formatPrice(value) {
  return `${Number(value || 0).toFixed(2)} EUR`;
}

function statusLabel(status) {
  return STATUS_LABELS[status] || status || "Submitted";
}

function notify(title, message) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.alert(`${title}\n\n${message}`);
    return;
  }
  Alert.alert(title, message);
}

function confirmAction(title, message, confirmText, onConfirm) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    if (window.confirm(`${title}\n\n${message}`)) {
      onConfirm();
    }
    return;
  }

  Alert.alert(
    title,
    message,
    [
      { text: "Cancel", style: "cancel" },
      { text: confirmText, onPress: onConfirm },
    ],
  );
}

function ActionButton({ icon, label, onPress, variant = "secondary", disabled }) {
  return (
    <TouchableOpacity
      style={[
        styles.actionButton,
        variant === "primary" && styles.actionButtonPrimary,
        variant === "danger" && styles.actionButtonDanger,
        disabled && styles.disabledButton,
      ]}
      onPress={onPress}
      disabled={disabled}
    >
      <MaterialIcons
        name={icon}
        size={18}
        color={variant === "secondary" ? "#334155" : "white"}
      />
      <Text style={[styles.actionButtonText, variant !== "secondary" && styles.actionButtonTextPrimary]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

export default function OnlineDeliveryItem(order) {
  const colorScheme = useColorScheme();
  const [token, setToken] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setToken((await AsyncStorage.getItem("token")) || "");
        setOperatorId((await AsyncStorage.getItem("operatorId")) || "");
      } catch (ex) {}
    })();
  }, []);

  const [changeStatus] = useMutation(CHANGE_ONLINE_ORDER_STATUS);
  const [completeOrder] = useMutation(COMPLETE_ONLINE_ORDER);

  const contact = [order.customer?.name, order.customer?.phone].filter(Boolean).join(" — ");
  const address = order.channel === "delivery"
    ? [order.fulfillment?.street, order.fulfillment?.city].filter(Boolean).join(", ")
    : order.fulfillment?.pickupTime;

  const onStatus = async (status) => {
    setErrorMessage("");
    if (!operatorId || !token) {
      const message = "Please sign in again before updating this order.";
      setErrorMessage(message);
      notify("Missing operator session", message);
      return;
    }
    setBusy(true);
    try {
      await changeStatus({ variables: { token, operator: operatorId, orderId: order.id, status } });
    } catch (e) {
      const message = e.message || "Please try again.";
      setErrorMessage(message);
      notify("Status update failed", message);
    } finally {
      setBusy(false);
    }
  };

  const completeWithPayment = async (paymentType) => {
    setErrorMessage("");
    if (!operatorId || !token) {
      const message = "Please sign in again before completing this order.";
      setErrorMessage(message);
      notify("Missing operator session", message);
      return;
    }

    setBusy(true);
    try {
      await completeOrder({ variables: { token, operator: operatorId, orderId: order.id, paymentType } });
    } catch (e) {
      const message = e.message || "Please try again.";
      setErrorMessage(message);
      notify("Completion failed", message);
    } finally {
      setBusy(false);
    }
  };

  const onComplete = (paymentType) => {
    confirmAction(
      "Complete order",
      `Record ${paymentType} payment for ${order.orderNumber}?`,
      "Complete",
      () => completeWithPayment(paymentType),
    );
  };

  return (
    <View style={[styles.card, colorScheme === "dark" && styles.cardDark]}>
      <View style={styles.cardHeader}>
        <View style={styles.cardTitleBlock}>
          <Text style={styles.orderNumber}>{order.orderNumber || order.id.slice(0, 8)}</Text>
          <Text style={styles.metaText}>{contact || "No contact"}</Text>
          {!!address && <Text style={styles.metaText}>{address}</Text>}
        </View>
        <View style={styles.statusBlock}>
          <View style={styles.statusPill}>
            <Text style={styles.statusPillText}>{statusLabel(order.status)}</Text>
          </View>
          <Text style={styles.channelText}>{order.channel === "delivery" ? "Delivery" : "Pickup"}</Text>
        </View>
      </View>

      <View style={styles.items}>
        {(order.items || []).map((item, index) => (
          <View key={`${order.id}-${item.productId}-${index}`} style={styles.itemRow}>
            <View style={styles.itemTextBlock}>
              <Text style={styles.itemTitle}>{item.quantity} × {item.title}</Text>
              {!!item.variant && <Text style={styles.metaText}>{item.variant}</Text>}
              {!!item.optionTitles?.length && <Text style={styles.metaText}>{item.optionTitles.join(", ")}</Text>}
              {!!item.comment && <Text style={styles.metaText}>{item.comment}</Text>}
            </View>
            <Text style={styles.itemPrice}>{formatPrice(item.grossTotal)}</Text>
          </View>
        ))}
      </View>

      {!!order.fulfillment?.comment && (
        <View style={styles.noteBox}>
          <MaterialIcons name="notes" size={18} color="#64748b" />
          <Text style={styles.metaText}>{order.fulfillment.comment}</Text>
        </View>
      )}

      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>
          {order.paymentStatus === "paid" ? "Paid" : "Due at handoff"}
        </Text>
        <Text style={styles.totalValue}>{formatPrice(order.totals?.orderGross)}</Text>
      </View>

      {!!errorMessage && (
        <View style={styles.errorBox}>
          <MaterialIcons name="error-outline" size={18} color="#b91c1c" />
          <Text style={styles.errorText}>{errorMessage}</Text>
        </View>
      )}

      <View style={styles.actions}>
        {["submitted_unpaid", "awaiting_payment"].includes(order.status) && (
          <>
            <ActionButton icon="check" label="Accept" variant="primary" disabled={busy} onPress={() => onStatus("accepted")} />
            <ActionButton icon="close" label="Reject" variant="danger" disabled={busy} onPress={() => onStatus("rejected")} />
          </>
        )}
        {order.status === "accepted" && (
          <ActionButton icon="restaurant" label="Preparing" variant="primary" disabled={busy} onPress={() => onStatus("preparing")} />
        )}
        {["accepted", "preparing"].includes(order.status) && (
          <ActionButton icon="done-all" label="Ready" variant="primary" disabled={busy} onPress={() => onStatus("ready")} />
        )}
        {order.status === "ready" && order.channel === "delivery" && (
          <ActionButton icon="delivery-dining" label="Out" variant="primary" disabled={busy} onPress={() => onStatus("out_for_delivery")} />
        )}
        {["ready", "out_for_delivery"].includes(order.status) && (
          <>
            <ActionButton icon="payments" label="Pay Cash" disabled={busy} onPress={() => onComplete("cash")} />
          </>
        )}
        {ACTIVE_STATUSES.has(order.status) && (
          <ActionButton icon="cancel" label="Cancel" variant="danger" disabled={busy} onPress={() => onStatus("cancelled")} />
        )}
      </View>
    </View>
  );
}

export { ONLINE_DELIVERY_FIELDS };

const styles = StyleSheet.create({
  card: {
    flex: 1,
    minWidth: 0,
    margin: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 8,
    padding: 12,
    gap: 10,
    backgroundColor: "white",
  },
  cardDark: { borderColor: "#334155" },
  cardHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", gap: 10 },
  cardTitleBlock: { flex: 1, minWidth: 0 },
  orderNumber: { fontSize: 20, fontWeight: "900" },
  metaText: { color: "#64748b", fontSize: 13 },
  statusBlock: { alignItems: "flex-end", gap: 4 },
  statusPill: {
    minHeight: 30,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    backgroundColor: "#e0f2fe",
  },
  statusPillText: { color: "#0369a1", fontWeight: "800" },
  channelText: { fontSize: 12, color: "#64748b", fontWeight: "800", textTransform: "uppercase" },
  items: { gap: 4 },
  itemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
  },
  itemTextBlock: { flex: 1, minWidth: 0 },
  itemTitle: { fontWeight: "800" },
  itemPrice: { fontWeight: "800" },
  noteBox: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    padding: 10,
    borderRadius: 8,
    backgroundColor: "#f8fafc",
  },
  totalRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 10 },
  totalLabel: { color: "#64748b", fontWeight: "800" },
  totalValue: { fontSize: 18, fontWeight: "900" },
  errorBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#fecaca",
    backgroundColor: "#fef2f2",
  },
  errorText: { flex: 1, minWidth: 0, color: "#b91c1c", fontWeight: "700" },
  actions: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  actionButton: {
    minHeight: 38,
    borderRadius: 8,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
  },
  actionButtonPrimary: {
    borderColor: Config.companyColor || "#2563eb",
    backgroundColor: Config.companyColor || "#2563eb",
  },
  actionButtonDanger: { borderColor: "#dc2626", backgroundColor: "#dc2626" },
  actionButtonText: { color: "#334155", fontWeight: "800" },
  actionButtonTextPrimary: { color: "white" },
  disabledButton: { opacity: 0.6 },
});
